import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Image as AntImage, Result, Space, Typography } from 'antd';
import {
  HeartFilled,
  HeartOutlined,
  MinusOutlined,
  PlusOutlined,
  ShoppingOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useProduct } from '@core/hooks/useCatalog';
import { useUiStore } from '@core/store/uiStore';
import { useWishlistStore } from '@core/store/wishlistStore';
import {
  useActiveCart,
  useAddToCart,
  useRemoveCartLine,
  useUpdateCartLine,
} from '@core/hooks/useCart';
import { useToast } from '@core/hooks/useToast';
import { flyToCart } from '@core/utils/flyToCart';
import { hasProductOptions } from '@core/utils/productOptions';
import { useCoffeePlaceholderSrc } from '@core/utils/coffeePlaceholder';
import Container from '@components/base/Container';
import Button from '@components/base/Button';
import LoadingState from '@components/base/LoadingState';
import ErrorState from '@components/base/ErrorState';
import ImageWithFallback from '@components/base/ImageWithFallback';
import Price from '@components/base/Price';
import AppModal from '@components/base/AppModal';
import { useRetainedWhileClosing } from '@components/base/AppModal/useRetainedWhileClosing';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useCopy } from '@/app/providers/useCopy';
import groceryPlaceholder from '@/assets/grocery-product-placeholder.webp';
import ProductOptionView from './ProductOptionView.jsx';

const { Title, Text } = Typography;

const PET_PRODUCT_PLACEHOLDER = '/pet/pet-placeholder.png';
const MIN_LOADER_MS = 500;

const DETAIL_THEME_COLORS = {
  default: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 24%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.36)',
  },
  demo: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 24%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.36)',
  },
  grocery: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 24%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.38)',
  },
  fish: {
    stepper: 'var(--color-primary)',
    stepperShadow: 'none',
    stepperButtonBg: 'rgba(255, 255, 255, 0.22)',
  },
  meat: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 22%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.36)',
  },
  restaurant: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 22%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.36)',
  },
  publication: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 20%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.32)',
  },
  pet: {
    stepper: 'var(--color-primary)',
    stepperShadow: '0 0.25rem 0.75rem color-mix(in srgb, var(--color-primary) 24%, transparent)',
    stepperButtonBg: 'rgba(255, 255, 255, 0.36)',
  },
};

function Pill({ children, tone = 'blue' }) {
  const colors = {
    red: { color: '#ef4444', border: '#ef4444', background: '#fff6f0' },
    blue: { color: '#1680bd', border: '#9ed5ef', background: '#f6fbff' },
    green: { color: '#0f766e', border: '#9bd8d0', background: '#eefbf8' },
  };
  const color = colors[tone] ?? colors.blue;

  return (
    <span
      className={`product-detail-pill product-detail-pill--${tone}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 21,
        padding: '0.125rem 0.5rem',
        borderRadius: 999,
        border: `0.125rem solid ${color.border}`,
        background: color.background,
        color: color.color,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

function DetailValue({ label, children }) {
  return (
    <div className="product-detail-fact">
      <Text
        className="product-detail-fact-label"
        style={{
          display: 'block',
          color: 'var(--color-text-secondary)',
          fontSize: 9,
          fontWeight: 800,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Text>
      <div
        className="product-detail-fact-value"
        style={{
          marginTop: 3,
          color: 'var(--color-text)',
          fontSize: 14,
          fontWeight: 800,
          lineHeight: 1.25,
        }}
      >
        {children ?? '-'}
      </div>
    </div>
  );
}

function DetailCartStepper({ value, disabled = false, onChange, themeKey = 'default' }) {
  const colors = DETAIL_THEME_COLORS[themeKey] ?? DETAIL_THEME_COLORS.default;
  const buttonStyle = {
    width: 28,
    height: 28,
    padding: 0,
    border: 0,
    borderRadius: '50%',
    background: colors.stepperButtonBg,
    color: '#ffffff',
    display: 'grid',
    placeItems: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 12,
    lineHeight: 1,
  };

  return (
    <div
      role="group"
      aria-label="Quantity"
      className="detail-cart-stepper"
      style={{
        minWidth: 102,
        padding: '0.1875rem 0.25rem',
        borderRadius: 999,
        background: colors.stepper,
        display: 'inline-grid',
        gridTemplateColumns: '1.75rem 2.375rem 1.75rem',
        alignItems: 'center',
        boxShadow: colors.stepperShadow,
      }}
    >
      <button
        type="button"
        aria-label="Decrease quantity"
        disabled={disabled || value <= 0}
        onClick={() => onChange?.(Math.max(0, value - 1))}
        style={{
          ...buttonStyle,
          opacity: disabled || value <= 0 ? 0.65 : 1,
        }}
      >
        <MinusOutlined />
      </button>
      <span
        aria-live="polite"
        style={{
          color: '#ffffff',
          fontSize: 14,
          fontWeight: 900,
          lineHeight: 1,
          textAlign: 'center',
        }}
      >
        {value}
      </span>
      <button
        type="button"
        aria-label="Increase quantity"
        disabled={disabled}
        onClick={() => onChange?.(value + 1)}
        style={buttonStyle}
      >
        <PlusOutlined />
      </button>
    </div>
  );
}

export default function ProductDetailView({
  productId,
  inModal = false,
  initialMode = 'detail',
  onDone,
  cartLine: initialCartLine,
}) {
  const { key: industryKey } = useIndustry();
  const coffeePlaceholderSrc = useCoffeePlaceholderSrc();
  const productQuery = useProduct(productId);
  // Whether the loading state should be sized for the options layout: the
  // clicked card's hint for this product, else the `options` open mode.
  const productModalHint = useUiStore((s) => s.productModalHint);
  const loadingHasOptions =
    productModalHint?.id === productId
      ? Boolean(productModalHint.hasOptions)
      : initialMode === 'options';
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(productId));
  const toast = useToast();
  const addToCart = useAddToCart();
  const updateCartLine = useUpdateCartLine();
  const removeCartLine = useRemoveCartLine();
  const { cart } = useActiveCart();
  const screens = Grid.useBreakpoint();
  const t = useCopy();
  const isMobile = !screens.md;
  const useTwoColumn = inModal && !isMobile;
  const [showOptions, setShowOptions] = useState(
    Boolean(initialMode === 'options' || (inModal && initialCartLine)),
  );
  const loadStartRef = useRef(null);
  const [minLoaderDone, setMinLoaderDone] = useState(!productQuery.isLoading);

  useEffect(() => {
    if (productQuery.isLoading) {
      loadStartRef.current = Date.now();
      setMinLoaderDone(false);
      return undefined;
    }
    if (loadStartRef.current == null) {
      setMinLoaderDone(true);
      return undefined;
    }
    const elapsed = Date.now() - loadStartRef.current;
    loadStartRef.current = null;
    const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
    const timer = setTimeout(() => setMinLoaderDone(true), remaining);
    return () => clearTimeout(timer);
  }, [productQuery.isLoading]);

  if (productQuery.isLoading || !minLoaderDone) {
    return inModal ? (
      // Class lets a skin size / skeleton the loading modal to match its layout;
      // `has-options` when the clicked card says the product has options.
      <div
        className={`product-detail-modal-loading ${loadingHasOptions ? 'has-options' : ''}`}
        style={{ minHeight: 560, background: 'var(--bg-page)' }}
      />
    ) : (
      <LoadingState minHeight={480} />
    );
  }

  if (productQuery.isError) {
    return (
      <Container size="md">
        <ErrorState error={productQuery.error} onRetry={productQuery.refetch} />
      </Container>
    );
  }

  const product = productQuery.data;
  if (!product) {
    return (
      <Result
        status="404"
        title={t('productNotFound', 'Product not found')}
        extra={
          inModal ? null : (
            <Link to="/catalog">
              <Button type="primary">{t('backToCatalog', 'Back to catalog')}</Button>
            </Link>
          )
        }
      />
    );
  }

  const optionedProduct = hasProductOptions(product);

  // In modal (desktop view useTwoColumn), always show ProductOptionView:
  // - If optionedProduct is true: renders Config model
  // - If optionedProduct is false: renders No-Config model
  if (useTwoColumn || showOptions) {
    return (
      <ProductOptionView
        product={product}
        onDone={onDone}
        onBack={inModal && !isMobile ? undefined : () => setShowOptions(false)}
        initialLine={initialCartLine}
      />
    );
  }

  const discount =
    product.mrp && product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : null;
  const savings = product.mrp && product.mrp > product.price ? product.mrp - product.price : null;
  const unit = product.uom ?? 'unit';

  const activeImage = product.imageUrl || (industryKey === 'grocery' ? groceryPlaceholder : null);
  const isOutOfStock = Boolean(product.isOutOfStock ?? !product.isAvailable);
  const cartLine = cart?.items?.find((line) => line.itemId === product.id);
  const handleAdd = async (e) => {
    try {
      if (optionedProduct) {
        setShowOptions(true);
        return;
      }
      flyToCart(e, product.imageUrl);
      await addToCart.mutateAsync({
        itemId: product.id,
        itemName: product.name,
        unitPrice: product.price,
        mrp: product.mrp,
        imageUrl: product.imageUrl,
        uom: product.uom,
        uomId: product.uomId,
        unit: product.uom,
        unitId: product.uomId,
        quantity: 1,
      });
      toast.success(`${product.name} added.`);
    } catch (err) {
      toast.apiError(err, 'Could not add to cart.');
    }
  };

  const cartCta = isOutOfStock ? (
    <span className="product-detail-stock-label">{t('outOfStock', 'Out of stock')}</span>
  ) : optionedProduct ? (
    <Button
      className={`product-detail-cart-btn ${industryKey}-detail-cart-btn`}
      type="primary"
      size={inModal && isMobile ? 'middle' : 'large'}
      icon={<ShoppingOutlined />}
      onClick={handleAdd}
    >
      {t('addToCart', 'Add to Cart')}
    </Button>
  ) : cartLine ? (
    <DetailCartStepper
      value={cartLine.quantity}
      themeKey={industryKey}
      disabled={updateCartLine.isPending || removeCartLine.isPending}
      onChange={(quantity) =>
        // Below 1 removes the line, so Add to Cart comes back.
        quantity < 1
          ? removeCartLine.mutate(cartLine.lineId)
          : updateCartLine.mutate({ lineId: cartLine.lineId, quantity })
      }
    />
  ) : (
    <Button
      className={`product-detail-cart-btn ${industryKey}-detail-cart-btn`}
      type="primary"
      size={inModal && isMobile ? 'middle' : 'large'}
      icon={<ShoppingOutlined />}
      loading={addToCart.isPending}
      onClick={handleAdd}
    >
      {t('addToCart', 'Add to Cart')}
    </Button>
  );
  const favouriteCta = (
    <Button
      className={`product-detail-save-btn ${industryKey}-detail-save-btn`}
      size={inModal && isMobile ? 'middle' : 'large'}
      icon={isWishlisted ? <HeartFilled /> : <HeartOutlined />}
      onClick={() => toggleWishlist(product)}
      aria-pressed={isWishlisted}
      style={{
        color: isWishlisted ? 'var(--color-primary)' : undefined,
      }}
    >
      {t('saveItem', 'Save item')}
    </Button>
  );

  return (
    <Container
      className={`product-detail-container ${inModal ? 'in-modal' : ''} ${
        isMobile ? 'is-mobile' : ''
      } ${useTwoColumn ? 'is-two-column' : ''} ${industryKey}-product-detail-container`}
      size="full"
      padded={false}
    >
      <section
        className={`product-detail-shell ${industryKey}-product-detail-shell`}
        style={{
          position: 'relative',
          marginTop: isMobile ? '' : 0,
          minHeight: useTwoColumn ? 500 : isMobile ? 'auto' : 560,
          overflow: 'hidden',
          borderRadius: inModal ? 0 : 8,
          background: '#ffffff',
          color: '#150d08',
          display: useTwoColumn ? 'grid' : 'block',
          gridTemplateColumns: useTwoColumn ? 'minmax(0, 1fr) minmax(0, 1fr)' : undefined,
        }}
      >
        <div
          className={`grocery-product-detail-image-container ${industryKey}-product-detail-image-container`}
          style={{
            position: 'relative',
            minHeight: useTwoColumn ? 500 : isMobile ? 180 : 256,
            maxHeight: useTwoColumn ? 500 : 400,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {activeImage ? (
            <AntImage
              src={activeImage}
              fallback={
                industryKey === 'coffee'
                  ? coffeePlaceholderSrc
                  : industryKey === 'fish'
                    ? '/fish-theme/fish-product-placeholder.png'
                    : industryKey === 'pet'
                      ? PET_PRODUCT_PLACEHOLDER
                      : industryKey === 'grocery'
                        ? groceryPlaceholder
                        : industryKey === 'meat'
                          ? '/meat/product-meat.webp'
                          : undefined
              }
              alt={product.name}
              width="100%"
              height="100%"
              style={{ objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
              wrapperStyle={{
                width: '100%',
                height: '100%',
                display: 'block',
              }}
              preview={{
                src: activeImage,
                mask: (
                  <Space direction="vertical" align="center" size={4}>
                    <EyeOutlined style={{ fontSize: '1.75rem', color: '#fff' }} />
                    <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 600 }}>
                      {t('viewFullImage', 'View Image')}
                    </span>
                  </Space>
                ),
              }}
            />
          ) : (
            <ImageWithFallback
              src={null}
              alt={product.name}
              aspectRatio={inModal ? 'auto' : '16 / 10'}
              radius="0"
              fallbackSrc={
                industryKey === 'coffee'
                  ? coffeePlaceholderSrc
                  : industryKey === 'fish'
                    ? '/fish-theme/fish-product-placeholder.png'
                    : industryKey === 'pet'
                      ? PET_PRODUCT_PLACEHOLDER
                      : industryKey === 'grocery'
                        ? groceryPlaceholder
                        : industryKey === 'meat'
                          ? '/meat/product-meat.webp'
                          : industryKey === 'default'
                            ? '/base/placeholder.jpg'
                            : undefined
              }
              style={inModal ? { height: '100%', width: '100%', objectFit: 'cover' } : undefined}
            />
          )}
          {activeImage ? (
            <div
              className="product-detail-thumb"
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 2,
                padding: 6,
                borderRadius: 10,
                background: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(0.5rem)',
                WebkitBackdropFilter: 'blur(0.5rem)',
                boxShadow: '0 0.375rem 1.125rem rgba(11, 26, 55, 0.12)',
              }}
            >
              <img
                src={activeImage}
                alt=""
                loading="lazy"
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  display: 'block',
                  border: '0.125rem solid #118ac3',
                  borderRadius: 6,
                }}
              />
            </div>
          ) : null}
        </div>

        <div
          className={`product-detail-content ${industryKey}-product-detail-content`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: inModal
              ? isMobile
                ? '0.375rem 0.875rem 1rem'
                : '1rem 1.5rem 2rem'
              : '1.25rem 1.625rem 3.25rem',
            width: '100%',
            minWidth: 0,
          }}
        >
          <div className="product-detail-scroll-area">
            <Title
              level={3}
              className="product-detail-title"
              style={{
                margin: '0 0 0.5rem',
                color: 'var(--color-text)',
                fontSize: 24,
                lineHeight: 1,
              }}
            >
              {product.name}
            </Title>

            <Space wrap size={[6, 6]} className="product-detail-pills">
              {product.foodType ? <Pill tone="red">{product.foodType}</Pill> : null}
              <Pill tone="green">per {unit}</Pill>
              {discount ? <Pill tone="red">{discount}% off</Pill> : null}
            </Space>

            <div className="product-detail-price-row">
              {product.price != null ? (
                <div>
                  <Text className="product-detail-price-label">
                    {t('sellingPrice', 'Selling Price')}
                  </Text>
                  <div className="product-detail-price-value">
                    <Price amount={product.price} />
                  </div>
                </div>
              ) : null}

              {product.mrp && product.mrp > product.price ? (
                <div>
                  <Text className="product-detail-price-label">{t('mrp', 'MRP')}</Text>
                  <div className="product-detail-mrp-value">
                    <Price amount={product.mrp} />
                  </div>
                </div>
              ) : null}

              {savings ? (
                <div className="product-detail-savings-container">
                  <span className="product-detail-savings-badge">
                    {t('save', 'Save')} <Price amount={savings} />
                  </span>
                </div>
              ) : null}
            </div>

            {product.description && (
              <div className="product-detail-description">
                <Text className="product-detail-description-header">
                  {t('description', 'Description')}
                </Text>
                <div>{product.description}</div>
              </div>
            )}

            <div
              className="product-detail-divider"
              style={{
                height: 1,
                margin: '1rem 0 0.875rem',
                background: '#f3e1cf',
              }}
            />

            <div
              className="product-detail-facts"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 40,
                rowGap: 28,
              }}
            >
              <DetailValue label={t('category', 'Category')}>
                {product.categoryName && product.categoryName !== 'Root'
                  ? product.categoryName
                  : '--'}
              </DetailValue>
              <DetailValue label={t('foodType', 'Food type')}>
                {product.foodType ?? '--'}
              </DetailValue>
              <DetailValue label={t('uom', 'UOM')}>
                {unit}
                {product.uomDescription ? ` — ${product.uomDescription}` : ''}
              </DetailValue>
            </div>
          </div>

          <div
            className="product-detail-cta-row"
            style={{
              position: 'static',
              display: 'flex',
              gap: 10,
              padding: '2.25rem 0 0',
              background: 'transparent',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'space-evenly',
            }}
          >
            <div className="product-detail-cart-cta" style={{ flex: '0 0 auto', minWidth: 0 }}>
              {cartCta}
            </div>
            <div
              className="product-detail-save-cta"
              style={{
                width: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {favouriteCta}
            </div>
          </div>
        </div>
      </section>

      <AppModal
        kind="product-options"
        open={showOptions}
        footer={null}
        onCancel={() => setShowOptions(false)}
        destroyOnHidden
        centered={true}
        width={inModal && isMobile ? 'calc(100% - 1rem)' : 780}
        zIndex={4100}
        className={`product-options-modal${!optionedProduct ? ' has-no-config' : ''}`}
        styles={{
          body: { padding: 0 },
          content: { padding: 0, overflow: 'hidden', borderRadius: 16 },
        }}
      >
        <ProductOptionView
          product={product}
          onDone={() => {
            setShowOptions(false);
            onDone?.();
          }}
        />
      </AppModal>
    </Container>
  );
}

export function ProductDetailModal({ open, onCancel, productId, cartLine, initialMode, onDone }) {
  const screens = Grid.useBreakpoint();
  const isCartDrawerViewport = Boolean(screens.sm);

  // Keep the last product rendered while the modal animates closed (callers
  // clear `productId` as they close it, which blanked the body mid-animation).
  const current = useMemo(
    () => (open && productId ? { productId, initialMode, cartLine } : null),
    [open, productId, initialMode, cartLine],
  );
  const [content, releaseContent] = useRetainedWhileClosing(current);

  return (
    <AppModal
      kind="product"
      open={open}
      footer={null}
      onCancel={onCancel}
      afterClose={releaseContent}
      width={isCartDrawerViewport ? 780 : 'calc(100% - 2rem)'}
      centered={!isCartDrawerViewport}
      destroyOnHidden
      zIndex={4100}
      className="grocery-product-modal product-detail-modal"
      styles={{
        body: { padding: 0 },
        content: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: 6,
          background: '#ffffff',
          maxHeight: isCartDrawerViewport ? '80vh' : '100vh',
        },
      }}
    >
      {content?.productId ? (
        <ProductDetailView
          productId={content.productId}
          inModal
          initialMode={content.initialMode}
          cartLine={content.cartLine}
          onDone={onDone}
        />
      ) : null}
    </AppModal>
  );
}
